import moment, {Moment} from 'moment';
import libphonenumber from 'google-libphonenumber';
import {NgxCurrencyInputMode} from "ngx-currency";
import packageInfo from '../../../package.json';
import _ from "lodash";
import {environment} from "../../environments/environment";

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
const PNF = libphonenumber.PhoneNumberFormat;

export type DualAxisPagingMode = 'infinite' | 'pages';

export class Globals{
  public static readonly APP_NAME = document.body.dataset.title || 'TeamerTime';
  public static readonly DAY_HOURS = 8;
  // One entry per src/lang/<code>.json; a code without a file would 404 and blank the UI.
  public static readonly LANGUAGES = ['en', 'nl'];
  // A logged-out visitor's pick from the header switch; a user's own pick lives in their settings.
  public static readonly GUEST_LANGUAGE_KEY = 'guest-language';
  private static gmaps_loaded: boolean = false;

  public static readonly PagesMode: DualAxisPagingMode = 'pages';
  public static readonly InfiniteMode: DualAxisPagingMode = 'infinite';

  private static readonly DEFAULT_KEY = 'loading';
  public static loading = false;
  private static loadings = [];

  private static LOC_CHECK: number = 1.8e6;
  private static lastloc:{date:Date, loc:google.maps.LatLngLiteral} = {date:null, loc:null};

  public static get domainInfo(): {logo: string|null, splash: string|null, name: string}|null{
    const set = document.body.dataset;
    return {name: set.name, logo: set.logo, splash: set.splash};
  }

  public static toDomain(newdomain: string){
    const loc = window.location;
    const domains = loc.hostname.split('.');
    const coredomain = newdomain + '.' + domains.slice(Math.max(domains.length - 2, 0)).join('.');

    window.location.replace(loc.protocol + '//' + coredomain + loc.pathname);
  }

  public static formatPhone(number: string){
    if(number) {
      return phoneUtil.format(phoneUtil.parseAndKeepRawInput(number), PNF.INTERNATIONAL);
    }
    return null;
  }

  public static get version(): string{
    // buildToken (commit SHA in CI, 'dev' locally) busts the ngx-translate
    // `?v=` cache on /lang/*.json every deploy, so new i18n keys are picked up.
    return `${packageInfo.version}-${environment.buildToken}`;
  }

  public static storage(path?: string){
    return path ? document.body.dataset.storage + '/' + path : null;
  }

  public static get currency(){
    const opts = {
      prefix: '€',
      min: 0,
      // Natural, not Financial. Financial fills digits from the right, so a
      // user typing 95 into an hourly rate gets EUR 0.95 and has to type 9500
      // to mean 95. That is the right convention for cash-register entry,
      // where every amount has cents; it is the wrong one here, because every
      // field this config reaches holds a whole-euro amount: rates, salaries,
      // budgets, margins, member-assignment offered/cost. Natural reads the
      // digits as typed and takes a separator for the cents nobody usually
      // enters.
      inputMode: NgxCurrencyInputMode.Natural,
      nullable: true,
      allowNegative: false
    };
    return opts;
  }

  public static get isLoading(){
    return Globals.loading || Globals.loadings.length;
  }

  public static get whatLoading(){
    return Globals.loadings.length ? Globals.loadings[0] : (Globals.loading ? Globals.DEFAULT_KEY : null);
  }

  public static addLoading(key = null){
    Globals.loadings.push(key || Globals.DEFAULT_KEY);
    return Globals.isLoading;
  }

  public static removeLoading(key = null){
    Globals.loadings = Globals.loadings.filter(o => {
      return o !== (key || Globals.DEFAULT_KEY);
    });
    return Globals.isLoading;
  }

  public static clearLoadings(){
    Globals.loadings = [];
    return Globals.isLoading;
  }

  public static toISODate(date: Moment|Date|string): string{
    if(date) {
      return moment(date).format('YYYY-MM-DD');
    }
    return null;
  }

  public static fromDateString(date: string|Date|Moment): Date{
    if(date instanceof Date){
      return date;
    }
    else if(moment.isMoment(date)){
      // what the Moment-backed pickers return since #1175
      return date.toDate();
    }
    else if(date){
      const d = moment(date.substr(0, 10)); // removes TZ information
      return d.toDate();
    }
    return null;
  }

  // NbMomentDateService (active NbDateService since #1175) calls date.clone()
  // on whatever value it's given, which native Date objects don't have.
  // Controls bound to [nbDatepicker] must hold a Moment, not a Date.
  public static fromDateStringToMoment(date: string|Date): Moment{
    return date ? moment(Globals.fromDateString(date)) : null;
  }

  public static getRandomColor() {
    const palette = [
      '#1863DC', '#102C45', '#1A4A7A', '#0E3D73',
      '#2B5BB5', '#3B6FB5', '#4B82D9', '#1952A8',
      '#0A2E52', '#5074B8',
    ];
    return palette[Math.floor(Math.random() * palette.length)];
  }

  // A plain literal, not google.maps.LatLng: the location sort on list pages must not need the
  // Maps script (#1755). LatLng.toJSON() serialised to this same shape.
  public static getLocation(): Promise<google.maps.LatLngLiteral> {
    if(this.lastloc.date && (new Date()).getTime() - Globals.lastloc.date.getTime() < Globals.LOC_CHECK){
      return Promise.resolve(this.lastloc.loc);
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resp => {
          this.lastloc.loc = {lat: resp.coords.latitude, lng: resp.coords.longitude};
          this.lastloc.date = new Date();
          return resolve(this.lastloc.loc);
        },
        err => {
          this.lastloc.date = new Date();
          this.lastloc.loc = null;
          return resolve(this.lastloc.loc);
        });
    });
  }

  public static calculateBusinessDays(firstDate, secondDate){
    let day1 = moment(firstDate).startOf('day');
    let day2 = moment(secondDate).startOf('day');
    // EDIT : start at 1
    let adjust = 1;

    if((day1.dayOfYear() === day2.dayOfYear()) && (day1.year() === day2.year())){
      return 0;
    }

    if(day2.isBefore(day1)){
      const temp = day1;
      day1 = day2;
      day2 = temp;
    }

    //Check if first date starts on weekends
    if(day1.day() === 6) { //Saturday
      //Move date to next week monday
      day1.day(8);
    } else if(day1.day() === 0) { //Sunday
      //Move date to current week monday
      day1.day(1);
    }

    //Check if second date starts on weekends
    if(day2.day() === 6) { //Saturday
      //Move date to current week friday
      day2.day(5);
    } else if(day2.day() === 0) { //Sunday
      //Move date to previous week friday
      day2.day(-2);
    }

    const day1Week = day1.week();
    let day2Week = day2.week();

    //Check if two dates are in different week of the year
    if(day1Week !== day2Week){
      //Check if second date's year is different from first date's year
      if (day2Week < day1Week){
        day2Week += day1Week;
      }
      //Calculate adjust value to be substracted from difference between two dates
      // EDIT: add rather than assign (+= rather than =)
      adjust += -2 * (day2Week - day1Week);
    }

    return day2.diff(day1, 'days') + adjust;
  }

  public static makeList(items: any[], key: string): string{
    return items.map(o => {
      return _.get(o, key) + '\n';
    }).join(', ');
  }

  /** True when a single-line field's text is cut off by its box (#1380). */
  public static clipped(el: HTMLElement): boolean{
    return el.scrollWidth > el.clientWidth;
  }

  public static popup(url, title, w, h){
    // Fixes dual-screen position                             Most browsers      Firefox
    const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !==  undefined   ? window.screenTop  : window.screenY;

    const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
    const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft
    const top = (height - h) / 2 / systemZoom + dualScreenTop
    const newWindow = window.open(url, title,
      `
      scrollbars=yes,
      width=${w / systemZoom},
      height=${h / systemZoom},
      top=${top},
      left=${left}
      `
    );

    if (window.focus) newWindow.focus();
    return newWindow;
  }

  public static logo(item: any) {
    if(typeof item === 'string'){
      return Globals.storage(item);
    }
    return item?.logo?.preview ? Globals.storage(item.logo.preview) : (item?.provider_avatar || null);
  }

  public static url(value: string): string|null{
    if(value){
      return value.startsWith('http') ? value : 'http://' + value;
    }
    return null;
  }

  /**
   * Merge two arrays by ID, keeping existing items and adding new ones.
   * Items from `additions` are only added if their ID is not already in `current`.
   */
  public static mergeById<T extends { id: number }>(current: T[], additions: T[]): T[] {
    const ids = new Set(current.map(item => item.id));
    return [...current, ...additions.filter(item => !ids.has(item.id))];
  }
}
